/**
 * AffiliateProfile — view and edit the affiliate's own account.
 *
 * `UpdateAffiliateProfile` requires a password on every call, so an edit that
 * leaves the password alone resends the stored one; only a filled-in "new
 * password" replaces it. Email is the sign-in identifier and stays read-only.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import type {
  AffiliateProfile as AffiliateProfileModel,
  AffiliateReviewState,
} from '@wlb/shared/api/affiliate/affiliateTypes';
import {
  AFFILIATE_COUNTRIES,
  AFFILIATE_HOW_HEARD_OPTIONS,
  AFFILIATE_HOW_HEARD_OTHER,
  affiliateCountryIdFromName,
} from '@wlb/shared/constants';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import {
  affiliateHowHeardValue,
  splitAffiliateHowHeard,
} from '@wlb/shared/utils/affiliateReferralCode';
import {
  validateAffiliatePassword,
  validatePasswordMatch,
} from '@wlb/shared/utils/validation';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import { ListStatus } from '../components/ui/ListStatus';
import { PasswordInput } from '../components/ui/PasswordInput';
import { Skeleton, SkeletonGroup, skeletonKeys } from '../components/ui/Skeleton';
import { MailIcon } from '../components/ui/icons';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
import '../features/affiliate/affiliatePortal.css';

/** Review is a three-state decision, so a refused account is not "awaiting". */
const AFFILIATE_STATE_BADGE_KEYS: Record<AffiliateReviewState, string> = {
  approved: 'affiliate.profile.memberBadge',
  pending: 'affiliate.profile.pendingBadge',
  rejected: 'affiliate.profile.rejectedBadge',
};

interface FieldErrors {
  address1?: string;
  countryId?: string;
  howHeard?: string;
  howHeardSpecify?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export function AffiliateProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, auth } = useAffiliateSession();
  const affiliateUserId = session?.affiliateUserId ?? 0;

  const [profile, setProfile] = useState<AffiliateProfileModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [countryId, setCountryId] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [howHeardSpecify, setHowHeardSpecify] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const resetForm = useCallback((next: AffiliateProfileModel) => {
    const { option, specify } = splitAffiliateHowHeard(next.howDidYouHear);
    setAddress1(next.address1);
    setAddress2(next.address2);
    setCountryId(String(affiliateCountryIdFromName(next.country) ?? ''));
    setHowHeard(option);
    setHowHeardSpecify(specify);
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setFormError('');
  }, []);

  const load = useCallback(async () => {
    if (!affiliateUserId) return;
    setLoading(true);
    setLoadError('');
    try {
      const next = await affiliateService.getProfile(affiliateUserId, auth);
      setProfile(next);
      resetForm(next);
    } catch (caught) {
      setLoadError(getUserFacingErrorMessage(caught, t('affiliate.profile.loadError')));
    } finally {
      setLoading(false);
    }
  }, [affiliateUserId, auth, resetForm, t]);

  useEffect(() => {
    load();
  }, [load]);

  const isOther = howHeard === AFFILIATE_HOW_HEARD_OTHER;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    setFormError('');

    const next: FieldErrors = {};
    if (!address1.trim()) next.address1 = t('affiliate.validation.addressRequired');
    if (!countryId) next.countryId = t('affiliate.validation.countryRequired');
    if (!howHeard) next.howHeard = t('affiliate.validation.howHeardRequired');
    if (isOther && !howHeardSpecify.trim()) {
      next.howHeardSpecify = t('affiliate.validation.specifyRequired');
    }
    if (newPassword || confirmPassword) {
      const password = validateAffiliatePassword(newPassword);
      if (!password.valid) next.newPassword = t(password.message);
      const match = validatePasswordMatch(newPassword, confirmPassword);
      if (!match.valid) next.confirmPassword = t(match.message);
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    try {
      const updated = await affiliateService.updateProfile(
        {
          affiliateUserId: profile.affiliateUserId,
          email: profile.email,
          password: newPassword.trim() || profile.password,
          address1: address1.trim(),
          address2: address2.trim(),
          countryId: Number(countryId),
          howDidYouHear: affiliateHowHeardValue({
            option: howHeard,
            specify: howHeardSpecify,
          }),
        },
        auth,
      );
      setProfile(updated);
      resetForm(updated);
      setEditing(false);
      ToastService.show({ type: 'success', message: t('affiliate.profile.updated') });
    } catch (caught) {
      setFormError(
        getUserFacingErrorMessage(caught, t('affiliate.profile.updateFailed')),
      );
    } finally {
      setSaving(false);
    }
  };

  const clearError = (field: keyof FieldErrors) => {
    setErrors(current => (current[field] ? { ...current, [field]: undefined } : current));
  };

  const displayValue = (value: string) => value || t('common.notProvided');

  const howHeardDisplay = () => {
    if (!profile?.howDidYouHear) return t('common.notProvided');
    const { option, specify } = splitAffiliateHowHeard(profile.howDidYouHear);
    if (option !== AFFILIATE_HOW_HEARD_OTHER) {
      const match = AFFILIATE_HOW_HEARD_OPTIONS.find(item => item.value === option);
      return match ? t(match.labelKey) : option;
    }
    return t('affiliate.profile.howHeardOther', { value: specify });
  };

  return (
    <AffiliatePortalLayout
      headerActions={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(AFFILIATE_ROUTES.DASHBOARD)}
          aria-label={t('affiliate.accessibility.backToDashboard')}
        >
          {t('affiliate.dashboard.title')}
        </Button>
      }
    >
      <div className="affiliate-dashboard__header">
        <h1 className="affiliate-dashboard__title">{t('affiliate.profile.heading')}</h1>
        {!loading && !loadError && !editing && (
          <div className="affiliate-dashboard__actions">
            <Button variant="primary" size="sm" onClick={() => setEditing(true)}>
              {t('affiliate.profile.editProfile')}
            </Button>
          </div>
        )}
      </div>

      {loading && (
        <SkeletonGroup className="affiliate-dashboard__skeleton">
          {skeletonKeys(5).map(key => (
            <Skeleton key={key} className="affiliate-dashboard__skeleton-row" />
          ))}
        </SkeletonGroup>
      )}

      {!loading && loadError && (
        <ListStatus variant="error" message={loadError} onAction={load} />
      )}

      {!loading && !loadError && profile && (
        <div className="affiliate-profile">
          <div className="affiliate-profile__section affiliate-profile__identity">
            <MailIcon size={24} aria-hidden />
            <div className="affiliate-profile__identity-text">
              <p className="affiliate-profile__email">{profile.email}</p>
              <span
                className={`affiliate-status-pill affiliate-status-pill--${
                  profile.state === 'approved' ? 'active' : 'inactive'
                }`}
              >
                {t(AFFILIATE_STATE_BADGE_KEYS[profile.state])}
              </span>
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleSubmit} noValidate>
              <FormError
                variant="banner"
                message={formError}
                id="affiliate-profile-error"
              />

              <section className="affiliate-profile__section">
                <h2 className="affiliate-profile__section-title">
                  {t('affiliate.profile.accountInformation')}
                </h2>
                <div className="affiliate-profile__grid">
                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.emailLabel')}</span>
                    <input
                      className="auth-flow__input"
                      type="email"
                      value={profile.email}
                      readOnly
                      aria-describedby="affiliate-profile-email-hint"
                    />
                    <small id="affiliate-profile-email-hint">
                      {t('affiliate.profile.emailReadOnlyHint')}
                    </small>
                  </label>

                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.howHeardLabel')}</span>
                    <select
                      className={`affiliate-auth__select${
                        errors.howHeard ? ' affiliate-auth__select--error' : ''
                      }`}
                      value={howHeard}
                      onChange={event => {
                        setHowHeard(event.target.value);
                        clearError('howHeard');
                      }}
                      aria-invalid={Boolean(errors.howHeard)}
                      aria-describedby={
                        errors.howHeard ? 'affiliate-profile-howheard-error' : undefined
                      }
                    >
                      <option value="">{t('affiliate.howHeard.placeholder')}</option>
                      {AFFILIATE_HOW_HEARD_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </select>
                    <FormError
                      message={errors.howHeard}
                      id="affiliate-profile-howheard-error"
                    />
                  </label>

                  {isOther && (
                    <label className="auth-flow__field">
                      <span>{t('affiliate.howHeard.specifyLabel')}</span>
                      <input
                        className={`auth-flow__input${
                          errors.howHeardSpecify ? ' auth-flow__input--error' : ''
                        }`}
                        type="text"
                        value={howHeardSpecify}
                        onChange={event => {
                          setHowHeardSpecify(event.target.value);
                          clearError('howHeardSpecify');
                        }}
                        placeholder={t('affiliate.howHeard.specifyPlaceholder')}
                        aria-invalid={Boolean(errors.howHeardSpecify)}
                        aria-describedby={
                          errors.howHeardSpecify
                            ? 'affiliate-profile-specify-error'
                            : undefined
                        }
                      />
                      <FormError
                        message={errors.howHeardSpecify}
                        id="affiliate-profile-specify-error"
                      />
                    </label>
                  )}
                </div>
              </section>

              <section className="affiliate-profile__section">
                <h2 className="affiliate-profile__section-title">
                  {t('affiliate.profile.addressInformation')}
                </h2>
                <div className="affiliate-profile__grid">
                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.address1Label')}</span>
                    <input
                      className={`auth-flow__input${
                        errors.address1 ? ' auth-flow__input--error' : ''
                      }`}
                      type="text"
                      value={address1}
                      onChange={event => {
                        setAddress1(event.target.value);
                        clearError('address1');
                      }}
                      autoComplete="address-line1"
                      aria-invalid={Boolean(errors.address1)}
                      aria-describedby={
                        errors.address1 ? 'affiliate-profile-address1-error' : undefined
                      }
                    />
                    <FormError
                      message={errors.address1}
                      id="affiliate-profile-address1-error"
                    />
                  </label>

                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.address2Label')}</span>
                    <input
                      className="auth-flow__input"
                      type="text"
                      value={address2}
                      onChange={event => setAddress2(event.target.value)}
                      autoComplete="address-line2"
                    />
                  </label>

                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.countryLabel')}</span>
                    <select
                      className={`affiliate-auth__select${
                        errors.countryId ? ' affiliate-auth__select--error' : ''
                      }`}
                      value={countryId}
                      onChange={event => {
                        setCountryId(event.target.value);
                        clearError('countryId');
                      }}
                      autoComplete="country-name"
                      aria-invalid={Boolean(errors.countryId)}
                      aria-describedby={
                        errors.countryId ? 'affiliate-profile-country-error' : undefined
                      }
                    >
                      <option value="">{t('affiliate.signUp.countryPlaceholder')}</option>
                      {AFFILIATE_COUNTRIES.map(country => (
                        <option key={country.id} value={country.id}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                    <FormError
                      message={errors.countryId}
                      id="affiliate-profile-country-error"
                    />
                  </label>
                </div>
              </section>

              <section className="affiliate-profile__section">
                <h2 className="affiliate-profile__section-title">
                  {t('affiliate.profile.passwordManagement')}
                </h2>
                <div className="affiliate-profile__grid">
                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.newPassword')}</span>
                    <PasswordInput
                      className={`auth-flow__input${
                        errors.newPassword ? ' auth-flow__input--error' : ''
                      }`}
                      value={newPassword}
                      onChange={event => {
                        setNewPassword(event.target.value);
                        clearError('newPassword');
                      }}
                      placeholder={t('affiliate.profile.newPasswordPlaceholder')}
                      autoComplete="new-password"
                      aria-invalid={Boolean(errors.newPassword)}
                      aria-describedby={
                        errors.newPassword
                          ? 'affiliate-profile-newpassword-error'
                          : undefined
                      }
                    />
                    <FormError
                      message={errors.newPassword}
                      id="affiliate-profile-newpassword-error"
                    />
                  </label>

                  <label className="auth-flow__field">
                    <span>{t('affiliate.profile.confirmPassword')}</span>
                    <PasswordInput
                      className={`auth-flow__input${
                        errors.confirmPassword ? ' auth-flow__input--error' : ''
                      }`}
                      value={confirmPassword}
                      onChange={event => {
                        setConfirmPassword(event.target.value);
                        clearError('confirmPassword');
                      }}
                      placeholder={t('affiliate.profile.confirmPasswordPlaceholder')}
                      autoComplete="new-password"
                      aria-invalid={Boolean(errors.confirmPassword)}
                      aria-describedby={
                        errors.confirmPassword
                          ? 'affiliate-profile-confirmpassword-error'
                          : undefined
                      }
                    />
                    <FormError
                      message={errors.confirmPassword}
                      id="affiliate-profile-confirmpassword-error"
                    />
                  </label>
                </div>
              </section>

              <div className="affiliate-profile__form-actions">
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? t('common.buttons.saving') : t('common.buttons.save')}
                </Button>
                <Button
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    resetForm(profile);
                    setEditing(false);
                  }}
                >
                  {t('common.buttons.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <section className="affiliate-profile__section">
                <h2 className="affiliate-profile__section-title">
                  {t('affiliate.profile.accountInformation')}
                </h2>
                <dl className="affiliate-profile__grid">
                  <div className="affiliate-profile__row">
                    <dt>{t('affiliate.profile.emailLabel')}</dt>
                    <dd>{profile.email}</dd>
                  </div>
                  <div className="affiliate-profile__row">
                    <dt>{t('affiliate.profile.howHeardLabel')}</dt>
                    <dd>{howHeardDisplay()}</dd>
                  </div>
                </dl>
              </section>

              <section className="affiliate-profile__section">
                <h2 className="affiliate-profile__section-title">
                  {t('affiliate.profile.addressInformation')}
                </h2>
                <dl className="affiliate-profile__grid">
                  <div className="affiliate-profile__row">
                    <dt>{t('affiliate.profile.address1Label')}</dt>
                    <dd>{displayValue(profile.address1)}</dd>
                  </div>
                  <div className="affiliate-profile__row">
                    <dt>{t('affiliate.profile.address2Label')}</dt>
                    <dd>{displayValue(profile.address2)}</dd>
                  </div>
                  <div className="affiliate-profile__row">
                    <dt>{t('affiliate.profile.countryLabel')}</dt>
                    <dd>{displayValue(profile.country)}</dd>
                  </div>
                </dl>
              </section>
            </>
          )}
        </div>
      )}
    </AffiliatePortalLayout>
  );
}
